import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

import { apiUrl } from '@/config';

import { useUser } from './useUser';

let installed = false;

export const useErrorLogging = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initialize Sentry
    if (import.meta.env.VITE_SENTRY_DSN && !installed) {
      console.info('INIT ERROR LOGGING');

      const isProduction = window.location.host === 'app.driverai.com';
      const replaysSessionSampleRate = isProduction ? 0.05 : 1.0;
      const tracesSampleRate = isProduction ? 0.2 : 1.0;
      const apiHostname = apiUrl ? new URL(apiUrl).hostname : 'localhost';
      const escapedHostname = apiHostname.replace(/\./g, '\\.');
      const tracePropagationTargets: (string | RegExp)[] =
        apiHostname === 'localhost'
          ? ['localhost']
          : ['localhost', new RegExp(`^https?:\\/\\/[^/]*${escapedHostname}(\\/|:|$)`)];

      Sentry.init({
        environment: window.location.host.replace(/\./g, '-'),
        dsn: import.meta.env.VITE_SENTRY_DSN,
        ignoreErrors: [
          'Login required',
          'Unauthorized',
          'Authentication',
          'token expired',
          '401',
          '403',
          'Network Error',
          // CefSharp / automated link scanners (e.g. Outlook Safe Links) — spurious unhandled rejections, not app code
          'Object Not Found Matching Id',
          'ResizeObserver',
          'ChunkLoadError',
          'Loading chunk',
          // Session Replay (rrweb) logs this when DOM nesting is very deep (e.g. expanded codebase trees).
          // It is a recorder safeguard, not an application error; see WEBAPP-FRONTEND-AQ.
          /\[rrweb-snapshot\] DOM tree depth exceeded/,
          // Auth0 loginWithPopup: user closed the popup or didn't complete auth in time.
          // These are expected user-initiated cancellations, not app errors; see WEBAPP-FRONTEND-6C.
          'Popup closed',
          'popup_closed',
        ],
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
          Sentry.captureConsoleIntegration({
            levels: ['error', 'warn'],
          }),
        ],
        tracePropagationTargets,
        tracesSampleRate,
        replaysSessionSampleRate,
        replaysOnErrorSampleRate: 0.3,
      });

      installed = true;
    }
  }, []);
};

export const useLogUser = () => {
  const { userId } = useUser();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!userId || !Sentry.isInitialized()) return;

    Sentry.setUser({ id: userId });
  }, [userId]);
};
